<template>
  <v-dialog
    :value="value"
    max-width="560"
    content-class="menu-search-dialog"
    @input="close()">
    <v-card class="menu-search-card">
      <div class="menu-search-input d-flex align-center px-4">
        <v-icon class="mr-3">
          mdi-magnify
        </v-icon>
        <v-text-field
          ref="search"
          v-model.trim="query"
          hide-details
          flat
          solo
          background-color="transparent"
          :placeholder="$t('menuSearch.placeholder')"
          autocomplete="off"
          autofocus
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="enter()"
          @keydown.esc.prevent="close()" />
        <span class="menu-search-kbd">esc</span>
      </div>

      <template v-if="query">
        <v-divider />

        <div
          ref="list"
          class="menu-search-results">
          <div
            class="menu-search-section px-4 pt-2 pb-1">
            {{ query.trim() ? `${$t('menuSearch.results')} (${results.length})` : $t('menuSearch.recent') }}
          </div>

          <div
            v-if="!results.length"
            class="menu-search-empty px-4 py-8 text-center">
            {{ $t('menuSearch.empty') }}
          </div>

          <v-list
            v-else
            dense
            class="py-0">
            <v-list-item
              v-for="(item, index) in results"
              :key="`${item.to}-${index}`"
              ref="rows"
              :class="{ 'menu-search-active': index === selectedIndex }"
              @mouseenter="selectedIndex = index"
              @click="go(item)">
              <v-list-item-icon class="mr-3">
                <v-icon>{{ item.icon || 'mdi-circle-small' }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <v-list-item-title v-html="highlight(item.title)" />
                <v-list-item-subtitle class="menu-search-crumb">
                  {{ item.group ? `${item.group} › ${item.to}` : item.to }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-icon
                v-if="index === selectedIndex"
                small
                class="menu-search-enter">
                mdi-keyboard-return
              </v-icon>
            </v-list-item>
          </v-list>
        </div>

        <v-divider />

        <div class="menu-search-footer d-flex align-center px-4">
          <span class="mr-4"><v-icon x-small>mdi-arrow-up</v-icon><v-icon x-small>mdi-arrow-down</v-icon> {{ $t('menuSearch.navigate') }}</span>
          <span class="mr-4"><v-icon x-small>mdi-keyboard-return</v-icon> {{ $t('menuSearch.open') }}</span>
          <v-spacer />
          <span>{{ $t('menuSearch.roleHint') }}</span>
        </div>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { getSearchableMenus } from '@/assets/js/Authentication'

const RECENT_KEY = 'menuSearchRecent'
const RECENT_LIMIT = 5

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      query: '',
      allMenus: [],
      recent: [],
      selectedIndex: 0
    }
  },
  computed: {
    results () {
      const q = this.query.trim().toLowerCase()
      if (!q) {
        return this.recent
          .map((to) => this.allMenus.find((m) => m.to === to))
          .filter(Boolean)
      }
      return this.allMenus
        .map((item) => ({ item, score: this.score(item, q) }))
        .filter((entry) => entry.score >= 0)
        .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
        .map((entry) => entry.item)
    }
  },
  watch: {
    value (opened) {
      if (opened) {
        this.allMenus = getSearchableMenus(this.$router)
        this.recent = this.loadRecent()
        this.query = ''
        this.selectedIndex = 0
        this.$nextTick(() => {
          if (this.$refs.search) {
            this.$refs.search.focus()
          }
        })
      }
    },
    results () {
      this.selectedIndex = 0
    }
  },
  methods: {
    score (item, q) {
      const title = item.title.toLowerCase()
      if (title.startsWith(q)) {
        return 3
      }
      if (title.includes(q)) {
        return 2
      }
      const hay = `${title} ${(item.group || '').toLowerCase()} ${(item.keywords || []).join(' ').toLowerCase()}`
      if (hay.includes(q)) {
        return 1
      }
      return -1
    },
    highlight (title) {
      const q = this.query.trim()
      const escaped = title.replace(/[&<>"]/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'
      }[c]))
      if (!q) {
        return escaped
      }
      const safe = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return escaped.replace(new RegExp(`(${safe})`, 'ig'), '<mark class="menu-search-mark">$1</mark>')
    },
    move (delta) {
      if (!this.results.length) {
        return
      }
      const next = this.selectedIndex + delta
      this.selectedIndex = Math.max(0, Math.min(next, this.results.length - 1))
      this.scrollActiveIntoView()
    },
    scrollActiveIntoView () {
      this.$nextTick(() => {
        const { rows } = this.$refs
        const row = rows && rows[this.selectedIndex]
        if (row && row.$el && row.$el.scrollIntoView) {
          row.$el.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    enter () {
      const item = this.results[this.selectedIndex]
      if (item) {
        this.go(item)
      }
    },
    go (item) {
      this.saveRecent(item.to)
      if (this.$route.name !== item.to) {
        this.$router.push({ name: item.to })
      }
      this.close()
    },
    close () {
      this.$emit('input', false)
    },
    loadRecent () {
      try {
        const raw = window.localStorage.getItem(RECENT_KEY)
        const list = raw ? JSON.parse(raw) : []
        return Array.isArray(list) ? list : []
      } catch (e) {
        return []
      }
    },
    saveRecent (to) {
      const list = [to, ...this.recent.filter((r) => r !== to)].slice(0, RECENT_LIMIT)
      this.recent = list
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list))
      } catch (e) {
        // ignore storage errors (private mode, quota)
      }
    }
  }
}
</script>

<style>
.menu-search-dialog {
  align-self: flex-start;
  margin-top: 12vh;
}
.menu-search-input {
  height: 56px;
}
.menu-search-input .v-input {
  font-size: 16px;
}
.menu-search-kbd {
  font-size: 11px;
  border: 1px solid rgba(127, 127, 127, 0.4);
  border-radius: 4px;
  padding: 1px 6px;
  opacity: 0.7;
}
.menu-search-results {
  max-height: 56vh;
  overflow-y: auto;
}
.menu-search-section {
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.6;
}
.menu-search-empty {
  font-size: 14px;
  opacity: 0.6;
}
.menu-search-active {
  background-color: rgba(127, 127, 127, 0.12);
}
.menu-search-crumb {
  font-size: 11px !important;
  opacity: 0.6;
}
.menu-search-enter {
  opacity: 0.5;
}
.menu-search-mark {
  background: transparent;
  color: var(--v-primary-base, #1976d2);
  font-weight: 500;
}
.menu-search-footer {
  height: 40px;
  font-size: 11px;
  opacity: 0.7;
}
</style>
